import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client, { unwrap } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { openFile } from "@/lib/verificationDocs";

function TenantRow({ tenant, onDecide }) {
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);

  return (
    <tr className="border-b border-[#F3EFE9] hover:bg-[#FAF8F5] transition-colors" data-testid={`tenant-row-${tenant._id}`}>
      <td className="py-3 px-4">
        <p className="font-medium">{tenant.shopName}</p>
        {tenant.gstNumber && <p className="text-xs font-mono-spec text-[#6E6760] mt-0.5">GST {tenant.gstNumber}</p>}
        {tenant.address && <p className="text-xs text-[#8C8379] mt-0.5">{tenant.address}</p>}
      </td>
      <td className="py-3 px-4">{tenant.city || "—"}</td>
      <td className="py-3 px-4" data-testid={`tenant-documents-${tenant._id}`}>
        {(tenant.verificationDocuments || []).length === 0 ? (
          <span className="text-xs text-[#8C8379]">None</span>
        ) : (
          <ul className="space-y-1">
            {tenant.verificationDocuments.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  className="max-w-[180px] truncate text-left text-xs text-[#C85A32] hover:underline"
                  onClick={() => openFile(d.id).catch(() => toast.error("Couldn't open the document"))}
                >
                  {d.originalName}
                </button>
              </li>
            ))}
          </ul>
        )}
      </td>
      <td className="py-3 px-4">
        <StatusBadge status={tenant.status} />
      </td>
      <td className="py-3 px-4">
        {tenant.status === "PENDING_VERIFICATION" || tenant.status === "CHANGES_REQUESTED" ? (
          <div className="space-y-2">
            {showNotes && (
              <Textarea
                data-testid={`tenant-notes-input-${tenant._id}`}
                placeholder="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="text-xs"
              />
            )}
            <div className="flex gap-2">
              <Button size="sm" data-testid={`tenant-approve-button-${tenant._id}`} className="bg-[#C85A32] hover:bg-[#B04B26] text-white" onClick={() => onDecide(tenant._id, "APPROVED", notes)}>
                Approve
              </Button>
              <Button size="sm" variant="outline" data-testid={`tenant-changes-button-${tenant._id}`} onClick={() => (showNotes ? onDecide(tenant._id, "CHANGES_REQUESTED", notes) : setShowNotes(true))}>
                Request Changes
              </Button>
              <Button size="sm" variant="outline" className="text-red-600" data-testid={`tenant-reject-button-${tenant._id}`} onClick={() => onDecide(tenant._id, "REJECTED", notes)}>
                Reject
              </Button>
            </div>
          </div>
        ) : (
          <span className="text-xs text-[#8C8379]">No action needed</span>
        )}
      </td>
    </tr>
  );
}

export default function PlatformTenantsPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("PENDING_VERIFICATION");

  const { data: platformSummary } = useQuery({
    queryKey: ["platform-summary"],
    queryFn: () => client.get("/dashboard/platform-summary").then(unwrap),
  });

  const { data: tenants = [] } = useQuery({
    queryKey: ["platform-tenants", tab],
    queryFn: () => client.get(`/platform/tenants?status=${tab === "ALL" ? "" : tab}`).then(unwrap),
  });

  const { data: pendingCatalogues = [] } = useQuery({
    queryKey: ["platform-catalogues-pending"],
    queryFn: () => client.get("/platform/catalogues/pending").then(unwrap),
  });

  const decideMutation = useMutation({
    mutationFn: ({ id, action, notes }) => client.patch(`/platform/tenants/${id}/decision`, { action, notes }),
    onSuccess: () => {
      toast.success("Decision recorded");
      queryClient.invalidateQueries({ queryKey: ["platform-tenants"] });
      queryClient.invalidateQueries({ queryKey: ["platform-summary"] });
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed"),
  });

  const catalogueDecideMutation = useMutation({
    mutationFn: ({ id, action }) => client.patch(`/platform/catalogues/${id}/decision`, { action }),
    onSuccess: () => {
      toast.success("Catalogue decision recorded");
      queryClient.invalidateQueries({ queryKey: ["platform-catalogues-pending"] });
      queryClient.invalidateQueries({ queryKey: ["platform-summary"] });
    },
  });

  return (
    <div data-testid="platform-admin-page">
      <h1 className="font-serif-display text-3xl text-[#2A211D]">Platform Overview</h1>
      <p className="text-sm text-[#6E6259] mt-2 mb-6">Verify shops and approve master catalogues.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="platform-stats-row">
        {[
          { label: "Businesses", value: platformSummary?.businesses, testId: "platform-stat-businesses" },
          { label: "Pending Verification", value: platformSummary?.pendingVerification, testId: "platform-stat-pending" },
          { label: "Catalogue Approvals", value: platformSummary?.catalogueApprovals, testId: "platform-stat-catalogue-approvals" },
          { label: "Active Products", value: platformSummary?.activeProducts, testId: "platform-stat-active-products" },
        ].map((s) => (
          <div key={s.label} data-testid={s.testId} className="bg-white border border-[#E6DDD3] rounded-xl p-5">
            <p className="text-[10px] font-semibold tracking-widest text-[#8C8379] uppercase mb-2">{s.label}</p>
            <p className="font-serif-display text-2xl text-[#2A211D]">{s.value ?? "—"}</p>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList data-testid="tenant-status-tabs">
          <TabsTrigger value="PENDING_VERIFICATION" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="CHANGES_REQUESTED" data-testid="tab-changes-requested">Changes Requested</TabsTrigger>
          <TabsTrigger value="APPROVED" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="REJECTED" data-testid="tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="ALL" data-testid="tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="w-full border border-[#E5DEC9] rounded-xl overflow-hidden bg-white shadow-sm mb-10">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F8F5F0] text-[#6E6760] font-mono-spec text-xs uppercase tracking-wider">
              <th className="py-3 px-4 text-left">Shop</th>
              <th className="py-3 px-4 text-left">City</th>
              <th className="py-3 px-4 text-left">Documents</th>
              <th className="py-3 px-4 text-left">Status</th>
              <th className="py-3 px-4 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <TenantRow key={t._id} tenant={t} onDecide={(id, action, notes) => decideMutation.mutate({ id, action, notes })} />
            ))}
            {tenants.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 text-center text-[#8C8379]">
                  No shops in this view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2 className="text-2xl font-bold font-heading text-[#2A211D] mb-4">Pending Catalogue Approvals</h2>
      <div className="w-full border border-[#E5DEC9] rounded-xl overflow-hidden bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F8F5F0] text-[#6E6760] font-mono-spec text-xs uppercase tracking-wider">
              <th className="py-3 px-4 text-left">Company</th>
              <th className="py-3 px-4 text-left">Version</th>
              <th className="py-3 px-4 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {pendingCatalogues.map((c) => (
              <tr key={c._id} className="border-b border-[#F3EFE9]" data-testid={`pending-catalogue-row-${c._id}`}>
                <td className="py-3 px-4 font-medium">{c.companyName}</td>
                <td className="py-3 px-4">{c.versionName}</td>
                <td className="py-3 px-4 flex gap-2">
                  <Button size="sm" data-testid={`catalogue-approve-button-${c._id}`} className="bg-[#C85A32] hover:bg-[#B04B26] text-white" onClick={() => catalogueDecideMutation.mutate({ id: c._id, action: "APPROVED" })}>
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" data-testid={`catalogue-reject-button-${c._id}`} onClick={() => catalogueDecideMutation.mutate({ id: c._id, action: "REJECTED" })}>
                    Reject
                  </Button>
                </td>
              </tr>
            ))}
            {pendingCatalogues.length === 0 && (
              <tr>
                <td colSpan={3} className="py-6 text-center text-[#8C8379]">
                  Nothing pending.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
